import React, { useLayoutEffect, useState } from 'react';

import './TimeDurationPicker.scss';
import cn from 'classnames';
import { Keys } from 'enums';

export interface ITimeDurationPickerProps {
    className?: string,
    disabled?: boolean,
    durationMilliseconds: number,
    label: string,
    onChange: (durationMilliseconds: number) => void,
}

export function TimeDurationPicker({ className, disabled, durationMilliseconds, label, onChange }: ITimeDurationPickerProps) {
    if (!durationMilliseconds) {
        durationMilliseconds = 0;
    }

    // Convert milliseconds to days, hours, minutes
    const minutesTotal = durationMilliseconds / (1000 * 60);
    const hoursTotal = minutesTotal / 60;
    const daysTotal = hoursTotal / 24;

    // We want to display each number as an integer.
    // For hours and minutes, we're really showing the remainder, not the total.
    // For example, if totalHours = 25, then we want to show "1 day, 1 hour".
    // So, we need to floor "days" to an int, convert it to hours, then subtract it from totalHours to get the remaining hours.
    const days = Math.floor(daysTotal);
    const hours = Math.floor(hoursTotal - (days * 24));
    const minutes = Math.floor(minutesTotal - (days * 24 * 60) - (hours * 60));

    // Strategy: we're only applying changes onBlur, because it's kind of obnoxious if we do it onChange,
    // because typing in the hours/minutes inputs is immediately applied, which can change the other inputs.
    // To do onBlur, we'll keep track of the input text values separately, then only parse them onBlur
    // Desired behavior:
    // - when duration changes: reset input vals
    // - onChange: save input vals, don't parse yet
    // - onBlur: parse values, call onChange
    const [daysText, setDaysText] = useState(days + '');
    const [hoursText, setHoursText] = useState(hours + '');
    const [minutesText, setMinutesText] = useState(minutes + '');

    // Using a layout effect instead of regular so that we resolve these before rendering.
    useLayoutEffect(() => {
        // Reset input values when the duration changes.
        setDaysText(days + '');
        setHoursText(hours + '');
        setMinutesText(minutes + '');
    }, [durationMilliseconds]);

    function onChangeDays(e) {
        setDaysText(e.target.value);
    }

    function onChangeHours(e) {
        setHoursText(e.target.value);
    }

    function onChangeMinutes(e) {
        setMinutesText(e.target.value);
    }

    function onBlur() {
        if (disabled) return;

        callOnChange();
    }

    function onKeyPress(e: React.KeyboardEvent) {
        if (disabled) return;

        // If the user presses enter, call our onChange handler with the current values
        if (e.key == Keys.Enter) {
            callOnChange();
        }
    }

    function callOnChange() {
        // If a value is invalid, we'll default to 0 so that we always stay valid and don't have to implement validation.
        let newDays = Number(daysText) || 0;
        let newHours = Number(hoursText) || 0;
        let newMinutes = Number(minutesText) || 0;

        const maxVal = 99999;
        newDays = Math.min(newDays, maxVal);
        newHours = Math.min(newHours, maxVal);
        newMinutes = Math.min(newMinutes, maxVal);

        // Combine parts and convert to each unit, all the way down to milliseconds.
        newHours += newDays * 24;
        newMinutes += newHours * 60;
        const newSeconds = newMinutes * 60;
        onChange(newSeconds * 1000);
    }

    return (<>
        <div className="formItem_label">{label}</div>
        <div className={cn("timeDurationPicker", className)}>
            <input
                className="timeDurationPicker_input"
                disabled={disabled}
                onBlur={onBlur}
                onChange={onChangeDays}
                onKeyPress={onKeyPress}
                type="text"
                value={daysText}
            />
            <span className="timeDurationPicker_label">days</span>
            <input
                className="timeDurationPicker_input"
                disabled={disabled}
                onBlur={onBlur}
                onChange={onChangeHours}
                onKeyPress={onKeyPress}
                type="text"
                value={hoursText}
            />
            <span className="timeDurationPicker_label">hours</span>
            <input
                className="timeDurationPicker_input"
                disabled={disabled}
                onBlur={onBlur}
                onChange={onChangeMinutes}
                onKeyPress={onKeyPress}
                type="text"
                value={minutesText}
            />
            <span className="timeDurationPicker_label">mins</span>
        </div>
    </>);
};